import { IonButton, IonButtons, IonCheckbox, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonListHeader, IonModal, IonRadio, IonRadioGroup, IonSelect, IonSelectOption, IonTitle, IonToolbar } from "@ionic/react"
import { closeOutline, closeSharp } from "ionicons/icons"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getPilgrimages, PilgrimageRecord } from "../utils/airtable-api"
import { fetchStages, PilgrimageState, selectCheckIns, selectPilgrimage, selectRoute, setPilgrimage } from "../features/pilgrimage/pilgrimageSlice"
import { selectUserName, setUserName, UserState } from "../features/user/userSlice"
import { fetchContent } from "../features/content/contentSlice"
import { LogMode, selectMode, setMode } from "../features/log/logSlice"

type InitialSetupProps = {
  show?: boolean | undefined;
  onDismiss?: () => void | undefined;
  pilgrimage: PilgrimageState;
  username: UserState;
  mode: LogMode;
}

export const InitialSetup = ({ show, onDismiss, pilgrimage, username, mode } : InitialSetupProps) => {
  const dispatch = useDispatch(),
    route = useSelector(selectRoute),
    checkins = useSelector(selectCheckIns),
    [pilgrimages, setPilgrimages] = useState<PilgrimageRecord[]>([]),
    [uniqueYears, setUniqueYears] = useState<number[]>([]),
    [usernameInput, setUserNameInput] = useState(username ?? ''),
    [year, setYear] = useState<number | null>(pilgrimage.year),
    [church, setChurch] = useState<string | null>(pilgrimage.church),
    [name, setName] = useState<string | null>(pilgrimage.name),
    [modeInput, setModeInput] = useState<LogMode>(mode),
    [memberOf, setMemberOf] = useState(pilgrimage.homeChurch === pilgrimage.church),
    [homeChurch, setHomeChurch] = useState<string | null>(pilgrimage.homeChurch);

  // When name changes, set pilgrimage ID and also name
  const chooseName = (pilgrimageName : string) => {
    // memoize
    if(name !== pilgrimageName) {
      const chosenPilgrimage = pilgrimages.find(p => p.fields.Year === year && p.fields.Church === church && p.fields.Name === pilgrimageName);
      if(chosenPilgrimage) {
        dispatch(fetchStages(chosenPilgrimage.id));
      }
      setName(pilgrimageName);
    }
  } 

  // Fetch list of all pilgrimages once on load
  useEffect(() => {
    const fetchData = async () => {
      const resp = await getPilgrimages();
      const resp_pilgrimages = resp.records,
        years = Array.from(new Set(resp_pilgrimages.map(p => p.fields.Year)));
      setPilgrimages(resp_pilgrimages);
      setUniqueYears(years);
      if(!year) {
        setYear(years[0]);
      }
      const startingChurch = resp_pilgrimages.filter(p => p.fields.Year === years[0])[0].fields.Church;
      if(!church) {
        setChurch(startingChurch);
        const startingName = resp_pilgrimages.filter(p => p.fields.Year === years[0] && p.fields.Church === startingChurch)[0].fields.Name;
        if(startingName !== name) {
          chooseName(startingName);
        }
      }
    };

    fetchData();
  }, [name]);

  // Submit chosen pilgrimage => Redux
  const submitForm = () => {
    const chosen = pilgrimages.find(p => p.fields.Year === year && p.fields.Church === church && p.fields.Name === name);
    if(chosen?.id) {
      dispatch(setUserName(usernameInput));
      dispatch(setPilgrimage({
        id: chosen.id,
        year,
        church,
        homeChurch: memberOf ? church : homeChurch,
        name,
        route,
        cached_route: route,
        checkins
      }));
      dispatch(setMode(modeInput));
      dispatch(fetchStages(chosen.id));
      dispatch(fetchContent(chosen.id));
    }
    if(onDismiss) {
      onDismiss();
    }
  }

  return <IonModal isOpen={show || (pilgrimage.year === null || pilgrimage.church === null || pilgrimage.name === null)}>
    <IonHeader>
      <IonToolbar>
        <IonTitle>
          Start your Pilgrimage
        </IonTitle>
        <IonButtons slot="end">
          <IonButton onClick={() => { onDismiss ? onDismiss() : (() => false)() }}>
            <IonIcon slot="icon-only" ios={closeOutline} md={closeSharp}/>
          </IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <IonList>
        <IonItem>
          <IonLabel position="stacked">Year</IonLabel>
          <IonSelect value={year} onIonChange={(e : CustomEvent) => setYear(e.detail.value)}>
            {uniqueYears.map(year => <IonSelectOption key={year} value={year}>{year}</IonSelectOption>)}
          </IonSelect>
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">Church</IonLabel>
          <IonSelect value={church} onIonChange={(e : CustomEvent) => setChurch(e.detail.value)}>
            {pilgrimages
              .filter(p => p.fields.Year === year)
              .map(p => <IonSelectOption key={p.id} value={p.fields.Church}>{p.fields.Church}</IonSelectOption>)}
          </IonSelect>
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">Pilgrimage Name</IonLabel>
          <IonSelect value={name} onIonChange={(e : CustomEvent) => chooseName(e.detail.value)}>
            {pilgrimages
              .filter(p => p.fields.Year === year && p.fields.Church === church)
              .map(p => <IonSelectOption key={p.id} value={p.fields.Name}>{p.fields.Name}</IonSelectOption>)}
          </IonSelect>
        </IonItem>
        <IonItem>
          <IonLabel>Member of {church}</IonLabel>
          <IonCheckbox checked={memberOf} onIonChange={(e : CustomEvent) => setMemberOf(e.detail.checked)}></IonCheckbox>
        </IonItem>
        {!memberOf && <IonItem>
          <IonLabel position="stacked">Home Church</IonLabel>
          <IonInput
            value={homeChurch}
            placeholder="Enter your home church or location"
            onIonChange={(e : CustomEvent) => setHomeChurch(e.detail.value)}
          />
        </IonItem>}
        <IonItem>
          <IonLabel position="stacked">Your Name</IonLabel>
          <IonInput
            value={usernameInput}
            onIonChange={(e : CustomEvent) => setUserNameInput(e.detail.value)}
            placeholder="Type your name here."
          />
        </IonItem>
        <IonRadioGroup onIonChange={(e) => setModeInput(e.detail.value)} value={modeInput}>
          <IonListHeader>
            <IonLabel>
              Pilgrimage Mode
            </IonLabel>
          </IonListHeader>
          <IonItem>
            <IonLabel>Walking, measured in miles</IonLabel>
            <IonRadio value="miles" />
          </IonItem>
          <IonItem>
            <IonLabel>Other activities, measured in points</IonLabel>
            <IonRadio value="points" />
          </IonItem>
        </IonRadioGroup>
      </IonList>
      <IonButton
        color="primary"
        class="begin-btn"
        onClick={() => submitForm()}
      >
        <IonLabel>Begin</IonLabel>
      </IonButton>
    </IonContent>
  </IonModal>
}